import React from 'react';

import './HumanAiSection.css';

/*
 * Human + AI collaboration — the parts of the product where people and
 * agents work as one team: organization setup, the shared kanban board
 * with human↔AI handoff, and human review gates.
 *
 * Static markup + CSS only, like the moat sections, so the content is
 * fully visible without JavaScript and indexable by crawlers.
 */

/* Real board column labels: task statuses include todo, ai_in_progress,
   human_in_progress, manual_review (shown as "Review") and done — with
   dedicated AI and human swimlanes. The hero card plays the handoff in
   BOTH directions: To Do → AI In Progress (robot, bar fills) → Human In
   Progress (human takes a decision) → back to AI In Progress (robot
   finishes) → Review (human) → Done. Pure CSS keyframes. */
const BOARD_COLUMNS = [
  { key: 'todo', label: 'To Do', ghosts: 2 },
  { key: 'ai', label: 'AI In Progress', ghosts: 1 },
  { key: 'human', label: 'Human In Progress', ghosts: 1 },
  { key: 'review', label: 'Review', ghosts: 1 },
  { key: 'done', label: 'Done', ghosts: 1 },
];

export const HandoffFigure: React.FC = () => (
  <figure
    className="ha-figure"
    aria-label="One kanban board with AI and human swimlanes: a task moves from To Do to AI In
      Progress, hands off to Human In Progress for a decision, returns to AI In Progress to
      finish, then Review under a human, then Done"
  >
    <div className="ha-board" aria-hidden="true">
      {BOARD_COLUMNS.map((c) => (
        <div key={c.key} className={`ha-col ha-col--${c.key}`}>
          <span className="ha-col__label">{c.label}</span>
          {Array.from({ length: c.ghosts }, (_, g) => (
            <span key={g} className={`ha-ghost${g % 2 ? ' ha-ghost--short' : ''}`} />
          ))}
        </div>
      ))}
      <div className="ha-hero">
        <span className="ha-hero__line" />
        <span className="ha-hero__meta">
          <span className="ha-hero__who">
            <i className="ha-hero__ai fa-solid fa-robot" />
            <i className="ha-hero__you fa-solid fa-user" />
          </span>
          <span className="ha-hero__bar"><span className="ha-hero__fill" /></span>
        </span>
      </div>
    </div>
    <figcaption className="ha-figure__caption">
      One task, handed both ways - an AI engineer builds, a human takes a decision, the agent
      picks the thread back up, a human reviews. Done.
    </figcaption>
  </figure>
);

const COLLAB_CARDS = [
  {
    icon: 'fa-solid fa-building-user',
    title: 'Org management, built in',
    body: 'Create an organization, invite by email, and assign owner, admin or member roles. Owners control billing and project access, admins manage people and settings, members build - and every project the org owns is shared with everyone in it.',
  },
  {
    icon: 'fa-solid fa-people-group',
    title: 'Multi-developer collaboration',
    body: 'Several developers work on one project at once: each takes tasks from the same board, works in an isolated cloud workspace or the VS Code extension, and merges by pull request into a shared repo - alongside the AI engineers, never blocked by them.',
  },
  {
    icon: 'fa-solid fa-arrows-turn-to-dots',
    title: 'Kanban handoff, human ↔ AI',
    body: 'Work moves across one board whether an agent or a teammate owns it. Assign any task to a human - a decision, a review, an API key - and agents pick the thread back up the moment it lands. List, sprint, Gantt and phase views included.',
  },
  {
    icon: 'fa-solid fa-user-check',
    title: 'Humans steer, agents ship',
    body: 'Review gates keep people in control: approve requirements, designs and plans, then agents carry the approved work through code, tests and deployment.',
  },
];

export const HumanAiSection: React.FC = () => (
  <section id="collaboration" className="ha-section" aria-labelledby="human-ai-heading">
    <div className="ha-inner">
      <div className="ha-header">
        <p className="ha-header__eyebrow">Collaboration</p>
        <h2 id="human-ai-heading" className="ha-header__heading">
          Humans and AI. One team.
          <br />
          <span className="ha-header__sub">
            Set up your org, add your developers, share the board, and hand work back and forth.
          </span>
        </h2>
      </div>
      <HandoffFigure />
      <div className="ha-cards">
        {COLLAB_CARDS.map((c) => (
          <article className="ha-card" key={c.title}>
            <i className={`ha-card__icon ${c.icon}`} aria-hidden="true" />
            <h3 className="ha-card__title">{c.title}</h3>
            <p className="ha-card__body">{c.body}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);
